import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  RefreshCw,
  Search,
  Shield,
  Plus,
  Eye,
  Trash2,
  PlayCircle,
  PauseCircle,
  Pencil,
  X,
  Loader2,
} from "lucide-react";

import { PERMS } from "../../../utils/perms";
import {
  useGetDepartmentsQuery,
  useCreateDepartmentMutation,
  useUpdateDepartmentMutation,
  useDeleteDepartmentMutation,
  useToggleDepartmentMutation,
  type Department,
} from "../api/departmentsApi";

import PageGate from "../../../components/PageGate";
import AccessGate from "../../../components/AccessGate";
import { useAccess } from "../../../utils/useAccess";

function safeMsg(err: any) {
  return err?.data?.message || err?.error || err?.message || "Something went wrong";
}

function StatCard({ title, value, hint }: { title: string; value: string; hint?: string }) {
  return (
    <div className="bg-white border rounded-xl p-5 shadow-sm">
      <div className="text-sm text-gray-600">{title}</div>
      <div className="text-2xl font-semibold text-gray-900 mt-1">{value}</div>
      {hint ? <div className="text-xs text-gray-500 mt-2">{hint}</div> : null}
    </div>
  );
}

function isActiveRow(row: any) {
  if (typeof row?.is_active === "boolean") return row.is_active;
  if (typeof row?.is_active === "number") return row.is_active === 1;
  const s = String(row?.status ?? "").toLowerCase();
  if (s === "active") return true;
  if (s === "inactive") return false;
  return false;
}

const EMPTY_FORM = { name: "", code: "", description: "" };

export default function DepartmentsPage() {
  const { user, can } = useAccess();

  const canCreate = can.perm(PERMS.department.create);
  const canUpdate = can.perm(PERMS.department.update);
  const canToggle = can.perm(PERMS.department.toggle);
  const canDelete = can.perm(PERMS.department.delete);

  const [q, setQ] = useState("");
  const [status, setStatus] = useState<"all" | "active" | "inactive">("all");

  const { data = [], isLoading, error, refetch } = useGetDepartmentsQuery();
  const [del, { isLoading: deleting }] = useDeleteDepartmentMutation();
  const [toggle, { isLoading: toggling }] = useToggleDepartmentMutation();
  const [create, { isLoading: creating }] = useCreateDepartmentMutation();
  const [update, { isLoading: updating }] = useUpdateDepartmentMutation();

  const busy = deleting || toggling;
  const saving = creating || updating;

  // ── modal state ──────────────────────────────────────────────────────
  const [editTarget, setEditTarget] = useState<Department | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);

  function openCreate() {
    setEditTarget(null);
    setForm(EMPTY_FORM);
    setFormError(null);
    setShowModal(true);
  }

  function openEdit(d: Department) {
    setEditTarget(d);
    setForm({ name: d.name ?? "", code: d.code ?? "", description: d.description ?? "" });
    setFormError(null);
    setShowModal(true);
  }

  function closeModal() {
    setShowModal(false);
    setEditTarget(null);
    setForm(EMPTY_FORM);
    setFormError(null);
  }

  async function submitForm(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name.trim() || !form.code.trim()) {
      setFormError("Name and code are required.");
      return;
    }
    setFormError(null);
    try {
      if (editTarget) {
        await update({ id: editTarget.id, data: form }).unwrap();
      } else {
        await create(form).unwrap();
      }
      closeModal();
    } catch (err) {
      setFormError(safeMsg(err));
    }
  }

  // ── filters ──────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const nq = q.trim().toLowerCase();
    return (data || []).filter((d: any) => {
      const blob = `${d?.name ?? ""} ${d?.code ?? ""} ${d?.slug ?? ""}`.toLowerCase();
      const okSearch = !nq || blob.includes(nq);
      const active = isActiveRow(d);
      const okStatus = status === "all" ? true : status === "active" ? active : !active;
      return okSearch && okStatus;
    });
  }, [data, q, status]);

  const stats = useMemo(() => {
    const total = filtered.length;
    const active = filtered.filter((d: any) => isActiveRow(d)).length;
    return { total, active, inactive: total - active };
  }, [filtered]);

  const onDelete = async (id: string) => {
    if (!canDelete) return;
    if (!window.confirm("Delete this department?")) return;
    try { await del(Number(id)).unwrap(); } catch (err) { alert(safeMsg(err)); }
  };

  const onToggle = async (id: string) => {
    if (!canToggle) return;
    try { await toggle(+id).unwrap(); } catch (err) { alert(safeMsg(err)); }
  };

  return (
    <PageGate perm={PERMS.department.access}>
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-white border rounded-xl p-5 shadow-sm">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-gray-50 border">
                <Shield className="w-5 h-5 text-gray-700" />
              </div>
              <div>
                <div className="font-semibold text-gray-900">Departments</div>
                <div className="text-sm text-gray-600">
                  Signed in as <span className="font-medium text-gray-900">{user?.email}</span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border bg-white hover:bg-gray-50 text-sm"
                onClick={() => refetch()}
                disabled={isLoading}
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>

              <AccessGate perm={PERMS.department.create}>
                <button
                  type="button"
                  onClick={openCreate}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-900 text-white hover:bg-gray-800 text-sm"
                >
                  <Plus className="w-4 h-4" />
                  Add Department
                </button>
              </AccessGate>
            </div>
          </div>

          {/* Filters */}
          <div className="mt-5 grid grid-cols-1 lg:grid-cols-12 gap-3 lg:items-end">
            <div className="lg:col-span-8">
              <label className="block text-[11px] font-semibold text-gray-500 uppercase mb-1">Search</label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search department name, code..."
                  className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-gray-200"
                />
              </div>
            </div>
            <div className="lg:col-span-3">
              <label className="block text-[11px] font-semibold text-gray-500 uppercase mb-1">Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as any)}
                className="w-full border rounded-lg px-3 py-2 text-sm bg-white outline-none focus:ring-2 focus:ring-gray-200"
              >
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div className="lg:col-span-1">
              <button
                type="button"
                onClick={() => { setQ(""); setStatus("all"); }}
                className="w-full px-4 py-2 border rounded-lg hover:bg-gray-50 text-sm"
              >
                Clear
              </button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard title="Total" value={String(stats.total)} />
          <StatCard title="Active" value={String(stats.active)} />
          <StatCard title="Inactive" value={String(stats.inactive)} />
          <StatCard title="My Admin Permissions" value={String(can.adminPerms.length)} hint="permissions starting with admin." />
        </div>

        {/* Table */}
        <div className="bg-white border rounded-xl p-6 shadow-sm">
          {isLoading ? (
            <div className="py-12 text-center text-gray-500">Loading…</div>
          ) : error ? (
            <div className="py-12 text-center text-red-600">{safeMsg(error)}</div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead>
                    <tr className="border-b text-gray-400 font-medium uppercase text-[11px]">
                      <th className="py-3 px-2 w-10">#</th>
                      <th className="py-3 px-4">Department</th>
                      <th className="py-3 px-4">Status</th>
                      <th className="py-3 px-4">Created</th>
                      <th className="py-3 px-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((d: any, index: number) => {
                      const active = isActiveRow(d);
                      return (
                        <tr key={d?.id} className="border-b hover:bg-gray-50 transition-colors">
                          <td className="py-4 px-2 text-gray-400 font-mono">{(index + 1).toString().padStart(2, "0")}</td>
                          <td className="py-4 px-4">
                            <div className="font-semibold text-gray-800">{d?.name ?? "—"}</div>
                            {d?.code ? <div className="text-[11px] text-gray-400 uppercase font-mono">{d.code}</div> : null}
                          </td>
                          <td className="py-4 px-4">
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>
                              {active ? "active" : "inactive"}
                            </span>
                          </td>
                          <td className="py-4 px-4 text-gray-600">{d?.created_at ? String(d.created_at).slice(0, 10) : "—"}</td>
                          <td className="py-4 px-4 text-right">
                            <div className="flex justify-end gap-2">
                              <AccessGate perm={PERMS.department.detailsAccess}>
                                <Link to={`/departments/${d?.id}`} className="p-1.5 border rounded hover:bg-gray-50" title="View">
                                  <Eye className="w-4 h-4 text-gray-600" />
                                </Link>
                              </AccessGate>

                              <AccessGate perm={PERMS.department.update}>
                                <button
                                  type="button"
                                  onClick={() => openEdit(d)}
                                  className="p-1.5 border rounded hover:bg-gray-50"
                                  title="Edit"
                                >
                                  <Pencil className="w-4 h-4 text-gray-600" />
                                </button>
                              </AccessGate>

                              <AccessGate perm={PERMS.department.toggle}>
                                <button
                                  type="button"
                                  onClick={() => onToggle(d?.id)}
                                  disabled={busy}
                                  className={`p-1.5 border rounded hover:bg-gray-50 ${busy ? "opacity-60 cursor-not-allowed" : ""}`}
                                  title={active ? "Deactivate" : "Activate"}
                                >
                                  {active ? <PauseCircle className="w-4 h-4 text-amber-600" /> : <PlayCircle className="w-4 h-4 text-green-600" />}
                                </button>
                              </AccessGate>

                              <AccessGate perm={PERMS.department.delete}>
                                <button
                                  type="button"
                                  onClick={() => onDelete(d?.id)}
                                  disabled={busy}
                                  className={`p-1.5 border rounded hover:bg-red-50 text-red-600 ${busy ? "opacity-60 cursor-not-allowed" : ""}`}
                                  title="Delete"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </AccessGate>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {filtered.length === 0 && (
                <div className="py-16 text-center text-gray-500 text-sm">No departments found.</div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Create / Edit modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={closeModal} />
          <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="font-semibold text-gray-900">{editTarget ? "Edit Department" : "Add Department"}</h2>
              <button onClick={closeModal} className="p-1 hover:bg-gray-100 rounded-full">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <form onSubmit={submitForm} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Name <span className="text-red-500">*</span></label>
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  disabled={saving}
                  required
                  className="w-full px-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-gray-200"
                  placeholder="e.g. Technical Services"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Code <span className="text-red-500">*</span></label>
                <input
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value })}
                  disabled={saving}
                  required
                  className="w-full px-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-gray-200"
                  placeholder="e.g. TECH"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  disabled={saving}
                  rows={3}
                  className="w-full px-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-gray-200 resize-none"
                  placeholder="Optional description"
                />
              </div>

              {formError && <p className="text-sm text-red-600">{formError}</p>}

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeModal}
                  disabled={saving}
                  className="flex-1 py-2 border rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-[2] py-2 bg-gray-900 text-white rounded-lg text-sm font-semibold hover:bg-gray-800 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  {saving ? (editTarget ? "Updating…" : "Creating…") : (editTarget ? "Update" : "Create")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </PageGate>
  );
}
